import { TextField } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { isFulfilled } from "@reduxjs/toolkit";
import { endOfYear, format, getMonth, getYear, startOfYear } from "date-fns";
import { useEffect } from "react";
import {
  ControllerRenderProps,
  FieldValues,
  FormProvider,
  useForm,
} from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { useAppDispatch } from "../../../../../../app/hooks";
import CommonTextField from "../../../../../../common/components/functional/commonTextField/CommonTextField";
import DialogContentWrapper from "../../../../../../common/components/functional/dialogs/DialogContentWrapper";
import isEmpty from "../../../../../../common/helpers/isEmpty";
import { Nullable } from "../../../../../../common/types/nullable";
import {
  AccumDateData,
  AccumDateRow,
  AccumulationCalculation,
  AccumulationsForEmploymentResponse,
} from "../../../../../employment/employmentApi";
import { addAccumulationDelta } from "../../../../../employment/employmentSlice";
import { AccumulationCalculationType } from "../../accumulationOptions";
import {
  formatPartitionKeyDisplayValue,
  isAccumulationCalculationMultiple,
  isAccumulationCalculationSingle,
  shouldIncludeAssignmentId,
} from "../../helpers";
import {
  AccumulationField,
  AccumulationType,
  EventSummary,
  InternalStateAccumulationsFormValues,
  SelectedAccumulation,
} from "../../types";
import AccumulationsGroupMathTable from "./AccumulationsGroupMathTable";
import AccumulationsMultiCalculationTable from "./AccumulationsMultiCalculationTable";
import AccumulationsSingleCalculationTable from "./AccumulationsSingleCalculationTable";

type Props = {
  tableData: AccumulationsForEmploymentResponse;
  onClose: () => void;
  contEmpId?: string;
  employmentId?: string;
  accumulationType: AccumulationType;
  selectedAccumulationRow: Nullable<AccumDateData>;
  getFields: (
    tableData: AccumulationsForEmploymentResponse,
    selectedAccumulation: SelectedAccumulation,
  ) => [AccumulationField[], AccumulationField[]];
  selectedAccumulation: Nullable<SelectedAccumulation>;
  updateLocalTableData: (date: string, data: EventSummary[]) => void;
  canEdit: boolean;
};

const Input = ({ ...props }) => (
  <CommonTextField
    {...props}
    {...(props?.inputType && { type: props.inputType })}
    autoComplete="off"
    sx={{ maxWidth: props?.maxWidth || "114px" }}
    size="small"
    inputProps={{
      ...props.inputProps,
    }}
  />
);

type NumericFormatValues = {
  formattedValue: string;
  value: string;
  floatValue: number | undefined;
};

const buildPartitionKeyFromDate = (date: Date) => {
  const start = format(startOfYear(date), "yyyy-MM-dd");
  const end = format(endOfYear(date), "yyyy-MM-dd");
  return `${end} ${start} ${end}`;
};

const AccumulationsEditTable = ({
  tableData,
  onClose,
  contEmpId,
  employmentId,
  accumulationType,
  getFields,
  selectedAccumulation,
  updateLocalTableData,
  selectedAccumulationRow,
  canEdit,
}: Props) => {
  const dispatch = useAppDispatch();

  const [keys, stateNames] = getFields(
    tableData,
    selectedAccumulation as SelectedAccumulation,
  );

  const includesAssignmentId = keys.some((key) => key.name === "assignmentId");

  const onSave = (fieldValues: FieldValues) => {
    if (!canEdit) return;

    const { modificationDate, assignmentId, rows } = fieldValues;

    const yearString = getYear(fieldValues?.year).toString();
    const monthString = (getMonth(fieldValues?.month) + 1).toString();
    const date = format(modificationDate, "yyyy-MM-dd");

    let values: Record<string, any> = {};

    // TODO: might need to be refactored if lots of different calculations are added
    if (
      selectedAccumulation?.calculationType ===
      AccumulationCalculationType.GROUP_MATH
    ) {
      // GROUP_MATH: Handle both ongoingAverages (lomakeskiansiolaskenta) and partitions (viikkokeskityömäärälaskenta)
      const groupMathValues: Record<string, any> = {};

      // Check if inputOperand (työviikot) was modified
      const inputOperandModified = dirtyFields.rows
        ? Object.values(dirtyFields.rows).some(
            (row: any) =>
              row &&
              Object.values(row).some(
                (field: any) => field && field.inputOperand,
              ),
          )
        : false;

      // Always include ongoingAverages (lomakeskiansiolaskenta)
      // GROUP_MATH rows have nested structure: [{ [key]: { valueDate, salary, workTime, inputOperand } }]
      const sortedRows = rows.sort(
        (a: Record<string, any>, b: Record<string, any>) => {
          // Extract the first object value from each row
          const aData = Object.values(a)[0] as any;
          const bData = Object.values(b)[0] as any;
          const dateA = new Date(aData.valueDate).getTime();
          const dateB = new Date(bData.valueDate).getTime();
          return dateA - dateB;
        },
      );

      const updatedOngoingAverages = sortedRows.reduce(
        (
          acc: Record<string, any>,
          current: Record<string, any>,
          index: number,
        ) => {
          let key: string;

          if (sortedRows.length === 1) {
            key = "NO_DATE";
          } else {
            key = index === 0 ? "1970-01-01" : "NO_DATE";
          }

          // Extract the data from the nested structure
          const currentData = Object.values(current)[0] as any;
          const formattedValue = {
            valueDate: format(new Date(currentData.valueDate), "yyyy-MM-dd"),
            salary: currentData.salary || "",
            workTime: currentData.workTime || "",
          };

          return {
            ...acc,
            [key]: formattedValue,
          };
        },
        {},
      );

      groupMathValues["lomakeskiansiolaskenta"] = JSON.stringify({
        ongoingAverages: updatedOngoingAverages,
      });

      // Include partitions only if inputOperand was modified
      if (inputOperandModified) {
        // Get the valueDate from the first row to determine the correct date range
        const firstRowData = Object.values(rows[0])[0] as any;
        const valueDate = firstRowData?.valueDate
          ? new Date(firstRowData.valueDate)
          : new Date();

        // Use the valueDate's year to determine date range
        // The partitionKey format is: [valueDate] [startDate] [endDate]
        // For viikkokeskityömäärälaskenta, start/end dates follow lomanmääräytymisvuosi logic
        // which starts April 1st and ends March 31st

        const valueDateYear = valueDate.getFullYear();
        const valueDateMonth = valueDate.getMonth();

        // Determine lomanmääräytymisvuosi range based on valueDate
        let startYear, endYear;
        if (valueDateMonth < 3) {
          // Before April, the accrual year started the previous April
          startYear = valueDateYear - 1;
          endYear = valueDateYear;
        } else {
          // April onwards, the accrual year started this April
          startYear = valueDateYear;
          endYear = valueDateYear + 1;
        }

        // Format dates: valueDate as-is, range from April 1 to March 31
        const lastDay = format(valueDate, "yyyy-MM-dd"); // valueDate as-is
        const firstDay = format(new Date(startYear, 3, 1), "yyyy-MM-dd"); // April 1
        const endDay = format(new Date(endYear, 2, 31), "yyyy-MM-dd"); // March 31

        const partitionKey = `${lastDay} ${firstDay} ${endDay}`;

        // Get inputOperand from first row (assuming all rows have same inputOperand for this calculation)
        const inputOperand = firstRowData?.inputOperand || "";

        groupMathValues["viikkokeskityömäärälaskenta"] = JSON.stringify({
          partitions: {
            [partitionKey]: {
              inputs: {
                "NoAssignment␞␞NoCustomer": firstRowData?.workTime || "", // Use workTime as input
              },
              inputOperand,
            },
          },
        });
      }

      values = groupMathValues;
    } else if (
      selectedAccumulation?.calculationType ===
      AccumulationCalculationType.MULTI_CALCULATION
    ) {
      if (isEmpty(tableData.data)) {
        // user is initializing the accumulation data
        // sort rows by valueDate
        const sortedRows = rows.sort(
          (a: Record<string, any>, b: Record<string, any>) => {
            const dateA = new Date(Object.values(a)[0].valueDate).getTime();
            const dateB = new Date(Object.values(b)[0].valueDate).getTime();
            return dateA - dateB;
          },
        );

        const updatedOngoingAverages = sortedRows.reduce(
          (
            acc: Record<string, any>,
            current: Record<string, any>,
            index: number,
          ) => {
            let key: string;

            if (sortedRows.length === 1) {
              // set key to null
              key = "NO_DATE";
            } else {
              // multiple rows
              // set key to 1970-01-01 for the first row (earliest date)
              // otherwise set key to "NO_DATE"
              key = index === 0 ? "1970-01-01" : "NO_DATE";
            }

            const value = Object.values(current)[0];

            const formattedValue = {
              ...value,
              valueDate: format(new Date(value.valueDate), "yyyy-MM-dd"),
            };

            // if TES engine/backend returns some other fields than known in the frontend, pass them as they are
            Object.keys(value).forEach((field) => {
              if (!["valueDate", "salary", "workTime"].includes(field)) {
                formattedValue[field] = value[field];
              }
            });

            return {
              ...acc,
              [key]: formattedValue,
            };
          },
          {},
        );

        // for multi-calculation add ongoingAverages property
        values = {
          [stateNames[0].name]: JSON.stringify({
            ongoingAverages: updatedOngoingAverages,
          }),
        };
      } else {
        // tableData.data is not empty -> user is not initializing the accumulation data
        values = {
          [stateNames[0].name]: JSON.stringify({
            ongoingAverages: rows.reduce(
              (acc: Record<string, any>, current: Record<string, any>) => {
                // get date key from the first object
                const key = Object.keys(current)[0];
                const value = current[key];

                const formattedValue = {
                  ...value,
                  valueDate: format(new Date(value.valueDate), "yyyy-MM-dd"),
                };

                // if TES engine/backend returns some other fields than known in the frontend, pass them as they are
                Object.keys(value).forEach((field) => {
                  if (!["valueDate", "salary", "workTime"].includes(field)) {
                    formattedValue[field] = value[field];
                  }
                });

                return {
                  ...acc,
                  [key]: formattedValue,
                };
              },
              {},
            ),
          }),
        };
      }
    } else if (
      selectedAccumulation?.calculationType ===
      AccumulationCalculationType.SINGLE_CALCULATION
    ) {
      // only one row for single-calculation
      const singleValues = rows[0];
      const partitionKey = buildPartitionKeyFromDate(
        modificationDate ?? new Date(),
      );
      values = {
        [stateNames[0].name]: JSON.stringify({
          partitionKey,
          workTime: singleValues.workTime || "",
          completedLimits: singleValues.completedLimits || 0,
        }),
      };
    }

    if (
      selectedAccumulation?.calculationType ===
      AccumulationCalculationType.GROUP_MATH
    ) {
      // GROUP_MATH: Create separate save call for each state
      const basePayload = {
        isInternal: true,
        date,
        contEmpId,
        ...(accumulationType === "employment" && { employmentId }),
        ...(fieldValues?.year && { year: yearString }),
        ...(fieldValues?.month && { month: monthString }),
        ...(fieldValues?.custOrgId && { custOrgId: fieldValues.custOrgId?.id }),
      };

      const savePromises: Promise<any>[] = [];
      const stateNames = Array.isArray(selectedAccumulation.stateNames)
        ? selectedAccumulation.stateNames
        : [selectedAccumulation.stateNames];

      // Create separate save call for each state that has data
      const scopes = Array.isArray(selectedAccumulation.scope)
        ? selectedAccumulation.scope
        : [selectedAccumulation.scope];

      stateNames.forEach((stateName, index) => {
        if (values[stateName]) {
          const scope = scopes[index];
          const payload = {
            ...basePayload,
            ...(shouldIncludeAssignmentId(scope) &&
              assignmentId?.id && {
                assignmentId: assignmentId.id,
              }),
            values: {
              [stateName]: values[stateName],
            },
          };

          savePromises.push(dispatch(addAccumulationDelta(payload)));
        }
      });

      Promise.all(savePromises).then((actions) => {
        if (actions.every((action) => isFulfilled(action))) {
          // Combine all response payloads
          const allPayloads = actions.flatMap((action) => action.payload);

          // Build combined object dynamically
          const combinedObject: any = {};

          allPayloads.forEach((item) => {
            const parsedValue = JSON.parse(item.payload.value);

            // Add state-specific data
            combinedObject[item.payload.stateName] = parsedValue;

            // Save metadata from first item
            if (!combinedObject.deltaId) {
              Object.assign(combinedObject, {
                ...(item.payload.month && { month: item.payload.month }),
                ...(item.payload.year && { year: item.payload.year }),
                ...(item.payload.assignmentId && {
                  assignmentId: assignmentId?.name,
                }),
                deltaId: item.id,
              });
            }
          });

          updateLocalTableData(date, [combinedObject]);
          onClose();
        }
      });
      return; // Exit early for GROUP_MATH
    }

    const addAccumDelta = {
      isInternal: true,
      date,
      contEmpId,
      ...(accumulationType === "employment" && { employmentId }),
      ...(fieldValues?.year && { year: yearString }),
      ...(fieldValues?.month && { month: monthString }),
      ...(fieldValues?.custOrgId && { custOrgId: fieldValues.custOrgId?.id }),
      ...(assignmentId?.id && {
        assignmentId: assignmentId.id,
      }),
      values,
    };

    dispatch(addAccumulationDelta(addAccumDelta)).then((action) => {
      if (isFulfilled(action)) {
        // Logic for MULTI_CALCULATION and SINGLE_CALCULATION
        const eventPayload = action.payload.map((item) => {
          const parsedValue = JSON.parse(item.payload.value);

          if (
            selectedAccumulation?.calculationType ===
            AccumulationCalculationType.MULTI_CALCULATION
          ) {
            const { ongoingAverages } = parsedValue;

            return {
              ...(item.payload.month && { month: item.payload.month }),
              ...(item.payload.year && { year: item.payload.year }),
              ...(item.payload.assignmentId && {
                assignmentId: assignmentId?.name,
              }),
              [item.payload.stateName]: {
                ongoingAverages, // parsed ongoingAverages includes all nested multi-calculation fields
              },
              deltaId: item.id,
            };
          } else if (
            selectedAccumulation?.calculationType ===
            AccumulationCalculationType.SINGLE_CALCULATION
          ) {
            const { partitionKey, workTime, completedLimits } = parsedValue;

            return {
              ...(item.payload.month && { month: item.payload.month }),
              ...(item.payload.year && { year: item.payload.year }),
              [item.payload.stateName]: {
                partitionKey,
                workTime,
                completedLimits,
              },
              deltaId: item.id,
            };
          }

          return {};
        });

        updateLocalTableData(date, eventPayload);
        onClose();
      }
    });
  };

  const getRows = () => {
    // by default, use selectedAccumulationRow with deltaId as default values
    const selectedRow = (selectedAccumulationRow?.rows.find(
      (row) => row.deltaId,
    ) ||
      // no deltas -> use first row
      selectedAccumulationRow?.rows[0] ||
      // no selectedAccumulationRow -> use default values from selectedAccumulation (accumulationOptions.ts)
      (selectedAccumulation?.defaultValues
        ? Array.isArray(selectedAccumulation.defaultValues)
          ? selectedAccumulation.defaultValues[0]?.rows[0]
          : selectedAccumulation.defaultValues.rows[0]
        : // the false branch should never execute
          undefined)) as AccumDateRow;

    // TODO: might need to be refaktored if lots of different calculations are added
    const calculations = [
      "keskiansiolaskenta", // multi-calculation
      "lomakeskiansiolaskenta", // multi-calculation
      "työajanlyhennyslaskenta", // single-calculation
      "vuosivapaalaskenta", // single-calculation
    ] as const;

    let result: Array<Record<string, any>> = [];

    // Special handling for GROUP_MATH
    if (
      selectedAccumulation?.calculationType ===
      AccumulationCalculationType.GROUP_MATH
    ) {
      // Get data from lomakeskiansiolaskenta (ongoingAverages)
      const lomakeskiansiolaskenta = selectedRow[
        "lomakeskiansiolaskenta"
      ] as AccumulationCalculation;
      const tyotuntienKeskimaara = selectedRow[
        "viikkokeskityömäärälaskenta"
      ] as AccumulationCalculation;

      if (
        lomakeskiansiolaskenta &&
        "ongoingAverages" in lomakeskiansiolaskenta
      ) {
        if (lomakeskiansiolaskenta.ongoingAverages) {
          const averages = Object.entries(
            lomakeskiansiolaskenta.ongoingAverages,
          ).map(([mapKeyDate, { valueDate, salary, workTime, ...rest }]) => {
            // Find matching inputOperand for this specific row
            let inputOperand = "";
            if (tyotuntienKeskimaara && "partitions" in tyotuntienKeskimaara) {
              const partitionKeys = Object.keys(
                tyotuntienKeskimaara.partitions || {},
              );

              let matchingKey;
              if (mapKeyDate === "NO_DATE") {
                // Match by valueDate or use single partition
                if (valueDate) {
                  const valueDateOnly =
                    typeof valueDate === "string"
                      ? valueDate.split("T")[0]
                      : valueDate;
                  matchingKey = partitionKeys.find((k) =>
                    k.startsWith(valueDateOnly),
                  );
                }
                if (!matchingKey && partitionKeys.length === 1) {
                  matchingKey = partitionKeys[0];
                }
              } else {
                // Match by mapKeyDate prefix
                matchingKey = partitionKeys.find((k) =>
                  k.startsWith(mapKeyDate),
                );
              }

              if (matchingKey) {
                inputOperand =
                  tyotuntienKeskimaara.partitions?.[matchingKey]
                    ?.inputOperand || "";
              }
            }

            return {
              [mapKeyDate]: {
                valueDate: valueDate || "",
                salary: salary || "0",
                workTime: workTime || "0",
                inputOperand,
                ...rest,
              },
            };
          });
          result = result.concat(averages);
        }
      }
    } else {
      // Original logic for non-GROUP_MATH types
      calculations.forEach((calcKey) => {
        if (
          calcKey in selectedRow &&
          typeof selectedRow[calcKey] === "object"
        ) {
          const calculationData = selectedRow[calcKey] as
            | string
            | AccumulationCalculation;

          if (isAccumulationCalculationMultiple(calculationData)) {
            if (calculationData.ongoingAverages) {
              const averages = Object.entries(
                calculationData.ongoingAverages,
              ).map(
                ([mapKeyDate, { valueDate, salary, workTime, ...rest }]) => ({
                  // e.g. "2021-10-01": { valueDate: "2021-10-01", salary: "1000", workTime: "7.5" }
                  [mapKeyDate]: {
                    valueDate: valueDate || "",
                    salary: salary || "0",
                    workTime: workTime || "0",
                    ...rest,
                  },
                }),
              );
              result = result.concat(averages);
            }
          }

          if (isAccumulationCalculationSingle(calculationData)) {
            // e.g. { partitionKey: "key", workTime: "7.5" }
            const singleCalculation = {
              partitionKey: calculationData.partitionKey || "",
              workTime: calculationData.workTime || "",
            };

            result.push({ ...singleCalculation });
          }
        }
      });
    }

    return result;
  };

  const rows = getRows();

  const methods = useForm<InternalStateAccumulationsFormValues>({
    defaultValues: {
      modificationDate: selectedAccumulationRow?.date
        ? new Date(selectedAccumulationRow?.date)
        : new Date(),
      assignmentId: null,
      rows: getRows(),
    },
    mode: "onChange",
  });

  const {
    handleSubmit,
    formState: { isDirty, isValid, dirtyFields },
  } = methods;

  const modificationDateValue = methods.watch("modificationDate");

  // Keep single-calculation partitionKey (laskentajakso) synced to modificationDate's year
  useEffect(() => {
    if (
      selectedAccumulation?.calculationType !==
      AccumulationCalculationType.SINGLE_CALCULATION
    ) {
      return;
    }

    const date = modificationDateValue ?? new Date();
    const partitionKey = buildPartitionKeyFromDate(date);
    const rows = methods.getValues("rows") as Array<Record<string, any>>;
    const current = rows?.[0]?.partitionKey;

    if (current !== partitionKey && rows?.[0]) {
      const updatedRows = [...rows];
      updatedRows[0] = { ...updatedRows[0], partitionKey };
      methods.setValue("rows", updatedRows as any, { shouldDirty: true });
    }
  }, [modificationDateValue, methods, selectedAccumulation?.calculationType]);

  const renderField = (
    subKey: string,
    field: ControllerRenderProps<InternalStateAccumulationsFormValues, any>,
  ) => {
    switch (subKey) {
      case "partitionKey":
        return (
          <TextField
            disabled
            {...field}
            value={formatPartitionKeyDisplayValue(field.value)}
            size="small"
          />
        );
      case "valueDate":
        return (
          <DesktopDatePicker
            {...field}
            value={field.value ? new Date(field.value) : null}
            format="dd.MM.yyyy"
            slotProps={{
              textField: {
                size: "small",
              },
            }}
          />
        );
      case "workTime":
      case "salary":
      case "inputOperand":
        return (
          <NumericFormat
            maxWidth="unset"
            fullWidth
            value={field.value}
            onValueChange={(values: NumericFormatValues) =>
              field.onChange(values.value)
            }
            valueIsNumericString
            allowNegative
            customInput={Input}
            decimalSeparator=","
          />
        );
      default:
        return <></>;
    }
  };

  const saveDisabled = !isDirty || !isValid;

  return (
    <DialogContentWrapper
      title="Korjaa kertymää"
      margin
      onCancel={onClose}
      onSave={handleSubmit(onSave)}
      customSaveButtonLabel="Tallenna"
      saveDisabled={saveDisabled}
    >
      <FormProvider {...methods}>
        {selectedAccumulation?.calculationType ===
        AccumulationCalculationType.GROUP_MATH ? (
          <AccumulationsGroupMathTable
            headers={Object.entries(rows[0])}
            renderField={renderField}
            isInitializing={isEmpty(tableData.data)}
            includesAssignmentId={includesAssignmentId}
            employmentId={employmentId as string}
          />
        ) : selectedAccumulation?.calculationType ===
          AccumulationCalculationType.MULTI_CALCULATION ? (
          <AccumulationsMultiCalculationTable
            headers={Object.entries(rows[0])}
            renderField={renderField}
            isInitializing={isEmpty(tableData.data)}
            includesAssignmentId={includesAssignmentId}
            employmentId={employmentId as string}
          />
        ) : (
          <AccumulationsSingleCalculationTable
            headers={Object.keys(rows[0])}
            renderField={renderField}
          />
        )}
      </FormProvider>
    </DialogContentWrapper>
  );
};

export default AccumulationsEditTable;
